import React, { ComponentProps, memo } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { File as FileIcon } from '@surglogs-app/icons'
import {
  Thumbnail,
  textEllipsis,
  Icon,
  FlexBox,
  TextButton,
} from '@surglogs-app/ui'
import { DialogLoader } from '@surglogs-app/ui.loader'

const FileContainer = styled(FlexBox)<{
  isThumbnail: boolean
  isInvalid?: boolean
}>`
  position: relative;
  align-items: center;
  flex: 1;
  color: ${({ theme }) => theme.ui.colors['navy.100']};
  border-color: ${({ theme, isThumbnail }) =>
    isThumbnail ? 'transparent' : theme.ui.colors['navy.25']};
  gap: ${({ theme }) => theme.ui.spacings.md};
  overflow: hidden;

  ${({ theme, isInvalid }) =>
    isInvalid
      ? css`
          color: ${theme.ui.colors['red.100']};
          border-color: ${theme.ui.colors['red.100']};
        `
      : css`
          &:hover {
            color: ${theme.ui.colors['blue.100']};
            border-color: ${theme.ui.colors['blue.100']};
          }
        `};
`

const StyledButton = styled(TextButton)`
  ${textEllipsis};
`

const ThumbnailContainer = styled(FlexBox)`
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.ui.spacings.xxl};
  height: ${({ theme }) => theme.ui.spacings.xxl};
  position: relative;
  background-color: ${({ theme }) => theme.ui.colors['white.100']};
  border: 1px solid;
  border-color: inherit;
  border-radius: ${({ theme }) => theme.ui.spacings.sm};
  overflow: hidden;
  color: inherit;
`

interface FileProps
  extends Omit<ComponentProps<typeof FileContainer>, 'isThumbnail'> {
  url: string | null
  fileName: string | null
  thumbnailUrl?: string
  isInvalid?: boolean
}

export const File = memo(
  ({ url, thumbnailUrl, fileName, isInvalid, ...rest }: FileProps) => {
    const isThumbnail = !!thumbnailUrl && !!url

    return (
      <FileContainer {...{ isThumbnail, isInvalid, ...rest }}>
        {!fileName && !url ? null : (
          <>
            <ThumbnailContainer>
              {isThumbnail ? (
                <Thumbnail {...{ src: thumbnailUrl, fallbackUrl: url }} />
              ) : (
                <Icon {...{ icon: FileIcon, color: 'inherit' }} />
              )}
            </ThumbnailContainer>
            {!url ? (
              <DialogLoader />
            ) : (
              <StyledButton
                {...{
                  as: 'a',
                  target: 'blank',
                  href: url,
                  color: 'inherit',
                  flex: 1,
                }}>
                {fileName}
              </StyledButton>
            )}
          </>
        )}
      </FileContainer>
    )
  },
)
